import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaCheck,
  FaMapMarkerAlt,
  FaTimes,
  FaVideo,
} from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Card from "../../components/Card";
import InfoRow from "../../components/InfoRow";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import RatingModal from "../../components/RatingModal";
import { useAuth } from "../../context/AuthContext";
import { sessionService } from "../../services/sessionService";
import { getStatusVariant } from "../../utils/styleUtils";

// dayjs extensions are now handled globally in dateUtils or main entry

export default function SessionPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [canRate, setCanRate] = useState(false);

  useEffect(() => {
    loadSession();
  }, []);

  const loadSession = async () => {
    try {
      const response = await sessionService.getById(id);
      setSession(response.session);

      // Check if can rate after loading session
      if (response.session?.status === "completed") {
        checkIfCanRate();
      }
    } catch (error) {
      console.error("Error loading session:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkIfCanRate = async () => {
    try {
      const response = await sessionService.checkCanRate(id);
      if (response.canRate) {
        setCanRate(true);
        setShowRatingModal(true);
      }
    } catch (error) {
      console.error("Error checking rating status:", error);
    }
  };

  const handleRatingSubmit = async ({ rating, comment }) => {
    try {
      await sessionService.submitRating(id, { rating, comment });
      setShowRatingModal(false);
      setCanRate(false);
      toast.success("Rating submitted successfully!");
      await loadSession();
    } catch (error) {
      toast.error(error.error || "Failed to submit rating");
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      await sessionService.checkIn(id);
      await loadSession();
      toast.success("Checked in successfully!");
    } catch (error) {
      toast.error(error.error || "Failed to check in");
    } finally {
      setCheckingIn(false);
    }
  };

  const handleJoin = async () => {
    try {
      await sessionService.join(id);
      await loadSession();
      if (session?.meetingUrl) {
        window.open(session.meetingUrl, "_blank");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed to Join",
        text: error.error || "Failed to join session",
      });
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  if (!session) {
    return <div className="p-8">Session not found</div>;
  }

  const isStudent = user?.id === session.studentId?._id;
  const isTutor = user?.id === session.tutorId?._id;
  const canCheckIn =
    !session.attendance?.[isStudent ? "studentPresent" : "tutorPresent"];
  const sessionStarted = new Date() >= new Date(session.startAt);
  const sessionEnded = new Date() >= new Date(session.endAt);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card>
        <PageHeader title="Session Details" />

        <div className="space-y-4 mb-6">
          <InfoRow label="Subject" value={session.subjectId?.name} />

          <InfoRow label={isStudent ? "Tutor" : "Student"}>
            {isStudent
              ? session.tutorId
                ? `${session.tutorId.name?.first} ${session.tutorId.name?.last}`
                : "Unknown Tutor"
              : session.studentId
                ? `${session.studentId.name?.first} ${session.studentId.name?.last}`
                : "Unknown Student"}
          </InfoRow>

          <InfoRow label="Time">
            {dayjs.utc(session.startAt).local().format("MMM D, YYYY h:mm A")} -{" "}
            {dayjs.utc(session.endAt).local().format("h:mm A")}
          </InfoRow>

          <InfoRow label="Session Type">
            <span className="flex items-center gap-2">
              {session.sessionType === "online" ? (
                <>
                  <FaVideo className="flex-shrink-0" /> Online
                </>
              ) : (
                <>
                  <FaMapMarkerAlt className="flex-shrink-0" /> In-Person
                </>
              )}
            </span>
          </InfoRow>

          {session.sessionType === "in-person" && session.location && (
            <InfoRow label="Location" value={session.location} />
          )}

          {session.sessionType === "online" && session.meetingUrl && (
            <InfoRow label="Meeting Link">
              <a
                href={session.meetingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:underline"
              >
                {session.meetingUrl}
              </a>
            </InfoRow>
          )}

          <InfoRow label="Status">
            <Badge
              variant={getStatusVariant(session.status)}
              label={session.status.replace("_", " ").toUpperCase()}
              size="small"
              className="font-medium"
            />
          </InfoRow>

          <InfoRow label="Attendance">
            <div>
              <p className="flex items-center gap-2">
                Student:{" "}
                {session.attendance?.studentPresent ? (
                  <>
                    <FaCheck className="text-secondary flex-shrink-0" /> Present
                  </>
                ) : (
                  <>
                    <FaTimes className="text-accent flex-shrink-0" /> Absent
                  </>
                )}
              </p>
              <p className="flex items-center gap-2">
                Tutor:{" "}
                {session.attendance?.tutorPresent ? (
                  <>
                    <FaCheck className="text-secondary flex-shrink-0" /> Present
                  </>
                ) : (
                  <>
                    <FaTimes className="text-accent flex-shrink-0" /> Absent
                  </>
                )}
              </p>
            </div>
          </InfoRow>
        </div>

        <div className="flex gap-4 flex-wrap">
          {canCheckIn &&
            sessionStarted &&
            !sessionEnded &&
            session.sessionType === "in-person" && (
              <Button
                onClick={handleCheckIn}
                disabled={checkingIn}
                loading={checkingIn}
              >
                <FaMapMarkerAlt className="mr-2" /> Mark Arrival
              </Button>
            )}

          {session.sessionType === "online" && session.meetingUrl && (
            <div>
              <Button
                onClick={handleJoin}
                variant="secondary"
                disabled={session.status === "completed"}
              >
                <FaVideo className="mr-2" /> Join Meeting
              </Button>
              {session.status === "completed" && (
                <p className="text-sm text-text opacity-60 mt-2">
                  This session has been completed. Check your email to rate this
                  session.
                </p>
              )}
            </div>
          )}

          {(isTutor || user?.role === "admin") &&
            session.status === "ongoing" && (
              <Button
                onClick={async () => {
                  try {
                    await sessionService.complete(id);
                    await loadSession();
                    toast.success(
                      "Session marked as completed! Emails sent to both participants."
                    );
                  } catch (error) {
                    toast.error(error.error || "Failed to complete session");
                  }
                }}
                variant="secondary"
              >
                <FaCheck className="mr-2" /> Mark Complete
              </Button>
            )}
        </div>

        {session.status === "completed" && (
          <div className="mt-4 p-4 bg-success/15 border border-success/30 rounded-md">
            <p className="text-success font-semibold flex items-center gap-2">
              <FaCheck /> Session Completed
            </p>
            <p className="text-text opacity-80 text-sm mt-1">
              Rating requests have been sent via email to both participants.
            </p>
          </div>
        )}

        {!sessionStarted && (
          <div className="mt-4 p-4 bg-accent/15 rounded-md">
            <p className="text-accent">
              Session starts in {dayjs(session.startAt).fromNow()}.
              {session.sessionType === "in-person"
                ? " Please arrive at the location and mark your arrival within 10 minutes of the start time."
                : " Please check in within 10 minutes of the start time."}
            </p>
          </div>
        )}

        {session.sessionType === "in-person" &&
          session.status === "scheduled" &&
          sessionStarted &&
          !sessionEnded && (
            <div className="mt-4 p-4 bg-primary/15 border border-primary/30 rounded-md">
              <p className="text-primary font-semibold flex items-center gap-2">
                <FaMapMarkerAlt /> In-Person Session
              </p>
              <p className="text-text opacity-80 text-sm mt-1">
                Location: {session.location || "Not specified"}
              </p>
              <p className="text-text opacity-80 text-sm">
                Mark your arrival once you reach the location. The tutor can
                mark the session as complete when finished.
              </p>
            </div>
          )}

        <div className="mt-6">
          <Link
            to={user?.role ? `/${user.role}/dashboard` : "/"}
            className="text-primary hover:opacity-80 flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </Card>

      {/* Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        onSubmit={handleRatingSubmit}
        sessionInfo={{
          subject: session?.subjectId?.name,
          otherParty: isStudent
            ? `${session?.tutorId?.name?.first} ${session?.tutorId?.name?.last}`
            : `${session?.studentId?.name?.first} ${session?.studentId?.name?.last}`,
        }}
      />
    </div>
  );
}
